/**
 * SOLID Review
 * S — Strong. One concern: the LOCAL player's moves as data — the request a plan decision says to send,
 *     and the server's answer the follow-up decision applies.
 * O — Good. A new move is a request schema, maybe an answer schema, and two ops on the contract.
 * L — N/A — no inheritance hierarchy.
 * I — Good. One schema per request or answer.
 * D — Good. zod, the shared primitives and the table's dice schema.
 */
import { z } from 'zod';
import { Identifier, MeyerRank } from './primitives';
import { ClientDiceRoll } from './ClientTable';
import { PayloadActiveRound, PayloadRoomRoster } from './TableEvents';

/**
 * A MOVE IS TWO DECISIONS AROUND ONE SERVER CALL (frontend strict-ports plan, FE1, 2026-10-03).
 *
 * The server owns the game, so a local move cannot be decided and then saved as the backend's are. A
 * PLAN decision reads the table and says what to send: which endpoint (a blind pass or an open claim),
 * with which ids, or a refusal when the table is not ready. That choice used to be inline in click
 * handlers. The Application sends the request, OUTSIDE its serial section, and an ANSWERED decision
 * applies the server's answer to the table as it is when the answer arrives.
 *
 * Where a handler changed the table BEFORE its call (the lift clears the server's decider, Play Again
 * resets the lives), the plan decision returns those changes and the shell saves them first, in the same
 * order as before.
 *
 * Each answered decision is handed the request it answers: the handler's closure held the values it
 * sent, such as whether the claim went blind or which score was believed, and the table may have moved
 * on by the time the answer arrives.
 */

export const CommandNothing = z.object({}).meta({ id: 'CommandNothing' });

export const RequestStartGame = z.object({ roomId: Identifier, playerId: Identifier }).meta({ id: 'RequestStartGame' });
/** A roll, a peek or a lift: the round and the player. */
export const RequestRoundMove = z.object({ roundId: z.string(), playerId: Identifier }).meta({ id: 'RequestRoundMove' });
export const RequestAnnounce = z.object({
  roundId: z.string(),
  playerId: Identifier,
  score: MeyerRank,
  /** Not peeked: a blind pass, which the server refuses if it has recorded a peek (§28.62). */
  blind: z.boolean(),
}).meta({ id: 'RequestAnnounce' });
/** Believe the claim, or roll first after a reveal (`believeClaim` with a floor of 0). */
export const RequestBelieve = z.object({
  roundId: z.string(),
  playerId: Identifier,
  roomId: Identifier,
  /** The floor the next roller must beat: the believed score, or 0 to roll first. */
  minimumScore: MeyerRank,
}).meta({ id: 'RequestBelieve' });
/** Play Again: a new game whose first roller is the last loser. */
export const RequestPlayAgain = z.object({ roomId: Identifier, rollerId: z.string() }).meta({ id: 'RequestPlayAgain' });
export const RequestResync = z.object({ roomId: Identifier, roomCode: z.string() }).meta({ id: 'RequestResync' });
/** A read for one room: the lobby's poll and its roster load. */
export const RequestRoomRead = z.object({ roomId: Identifier }).meta({ id: 'RequestRoomRead' });
/** Leaving: what to tell the server, best effort — null when there is no seat to give up, or nobody to tell. */
export const DtoLeave = z.object({
  leave: z.object({ roomId: Identifier, playerId: Identifier }).nullable(),
}).meta({ id: 'DtoLeave' });

export const CommandAnnounce = z.object({ score: MeyerRank }).meta({ id: 'CommandAnnounce' });
export const CommandLeave = z.object({
  /** False for the room-ended overlay: the server closed the table, there is nothing to leave. */
  notifyServer: z.boolean(),
}).meta({ id: 'CommandLeave' });

/** A round the server opened (`Round`): `startRound` and `believeClaim` answer with it. */
export const AnswerRound = z.object({
  roundId: z.string().nullish(),
  rollerId: z.string().nullish(),
  minimumScore: MeyerRank.nullish(),
}).meta({ id: 'AnswerRound' });
/** An accepted claim (`ClaimEvent`): the decider the server derived and whether it went blind. */
export const AnswerClaim = z.object({
  challengerId: z.string().nullish(),
  wasBlind: z.boolean().nullish(),
}).meta({ id: 'AnswerClaim' });

export const CommandGameStarted = z.object({ request: RequestStartGame, answer: AnswerRound }).meta({ id: 'CommandGameStarted' });
export const CommandRoundMoveAnswered = z.object({ request: RequestRoundMove }).meta({ id: 'CommandRoundMoveAnswered' });
export const CommandPeeked = z.object({ request: RequestRoundMove, answer: ClientDiceRoll }).meta({ id: 'CommandPeeked' });
export const CommandClaimAccepted = z.object({ request: RequestAnnounce, answer: AnswerClaim }).meta({ id: 'CommandClaimAccepted' });
export const CommandBelieveAccepted = z.object({ request: RequestBelieve, answer: AnswerRound }).meta({ id: 'CommandBelieveAccepted' });
/** Play Again's answer — null when the server refused; the table records the activity either way. */
export const CommandPlayAgainAnswered = z.object({ request: RequestPlayAgain, answer: AnswerRound.nullable() }).meta({ id: 'CommandPlayAgainAnswered' });
/** A resync's first answer: the table's live round from the restore read, null between rounds. */
export const CommandActiveRoundRestored = z.object({ request: RequestResync, activeRound: PayloadActiveRound.nullable() }).meta({ id: 'CommandActiveRoundRestored' });
/** A resync's second answer: who the room lists as suspended (`RoomCodeLookup.suspendedPlayers`; absent when the room is gone). */
export const CommandSuspendedReconciled = z.object({
  request: RequestResync,
  suspendedPlayers: z.array(z.object({ id: z.string(), isConnected: z.boolean() })).nullish(),
}).meta({ id: 'CommandSuspendedReconciled' });
/** The lobby poll's read of the active round (`getActiveRound`): only whether one started, and who rolls. */
export const PayloadLobbyRound = z.object({ roundId: z.string().nullable(), rollerId: z.string().nullable() }).meta({ id: 'PayloadLobbyRound' });
/** The lobby poll's answer — null when no round has started. */
export const CommandLobbyActiveRound = z.object({ request: RequestRoomRead, activeRound: PayloadLobbyRound.nullable() }).meta({ id: 'CommandLobbyActiveRound' });
/** The lobby's roster read on entry (`room`). */
export const CommandLobbyRoomLoaded = z.object({ request: RequestRoomRead, roster: PayloadRoomRoster }).meta({ id: 'CommandLobbyRoomLoaded' });

export type ICommandNothing = z.infer<typeof CommandNothing>;
export type ICommandAnnounce = z.infer<typeof CommandAnnounce>;
export type ICommandLeave = z.infer<typeof CommandLeave>;
export type ICommandGameStarted = z.infer<typeof CommandGameStarted>;
export type ICommandRoundMoveAnswered = z.infer<typeof CommandRoundMoveAnswered>;
export type ICommandPeeked = z.infer<typeof CommandPeeked>;
export type ICommandClaimAccepted = z.infer<typeof CommandClaimAccepted>;
export type ICommandBelieveAccepted = z.infer<typeof CommandBelieveAccepted>;
export type ICommandPlayAgainAnswered = z.infer<typeof CommandPlayAgainAnswered>;
export type ICommandActiveRoundRestored = z.infer<typeof CommandActiveRoundRestored>;
export type ICommandSuspendedReconciled = z.infer<typeof CommandSuspendedReconciled>;
export type ICommandLobbyActiveRound = z.infer<typeof CommandLobbyActiveRound>;
export type ICommandLobbyRoomLoaded = z.infer<typeof CommandLobbyRoomLoaded>;
export type IPayloadStartGame = z.infer<typeof RequestStartGame>;
export type IPayloadRoundMove = z.infer<typeof RequestRoundMove>;
export type IPayloadAnnounce = z.infer<typeof RequestAnnounce>;
export type IPayloadBelieve = z.infer<typeof RequestBelieve>;
export type IPayloadPlayAgain = z.infer<typeof RequestPlayAgain>;
export type IPayloadResync = z.infer<typeof RequestResync>;
export type IPayloadRoomRead = z.infer<typeof RequestRoomRead>;
export type IEntityLeave = z.infer<typeof DtoLeave>;
export type IPayloadLobbyRound = z.infer<typeof PayloadLobbyRound>;
